// Get a number for the voice agent (modal of /voice-numbers): pick one of the
// numbers Unanimity has ready (already routed to the platform), the agent
// that will answer it, and pay its monthly subscription on Stripe's page.
// Nobody types a number: a number we do not route would never reach the
// agent. Stripe sends the browser back to /voice-numbers?voice_number=paid
// &session_id=… (or ?voice_number=cancelled), handled by VoiceNumbers.js.
// API: POST /api/voice-numbers/checkout → { checkout_url }.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Lock, Phone } from 'lucide-react';
import { Banner, Field, Modal } from '../../design/ui';
import { fail, formatPrice, startCheckout } from './api';
import { ChoiceTiles } from './VoiceKit';
import { numberLabel } from './phoneFormat';

const BuyNumber = ({ offer, agents, defaultAgentId, onCancel }) => {
    const { t, i18n } = useTranslation();
    const numbers = offer?.numbers || [];
    const [chosen, setChosen] = useState(numbers[0]?.phone_number || '');
    const [agentId, setAgentId] = useState(defaultAgentId || agents[0]?.id || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const price = formatPrice(offer?.monthly_price_eur, i18n.language);

    let blocked = '';
    if (!offer?.purchasable) {
        blocked = t('voiceSetup.buy.notPurchasable', 'Ce compte ne peut pas acheter de numéro en ligne : contactez le support.');
    } else if (!offer?.billing_ready) {
        blocked = t('voiceSetup.buy.billingUnavailable', 'Le paiement en ligne n’est pas disponible pour le moment : contactez le support.');
    } else if (numbers.length === 0) {
        blocked = t('voiceSetup.buy.noneAvailable', 'Aucun numéro disponible pour le moment : un nouveau lot est en commande. Revenez dans quelques heures.');
    }

    const pay = async () => {
        if (!chosen || busy) return;
        setBusy(true);
        setError('');
        try {
            const res = await startCheckout({ phone_number: chosen, agent_id: agentId || null });
            window.location.assign(res.data.checkout_url);
        } catch (err) {
            setError(fail(err, t('voiceSetup.buy.error', 'Le paiement n’a pas pu être préparé. Réessayez dans un instant.')));
            setBusy(false);
        }
    };

    return (
        <Modal
            wide
            icon={Phone}
            title={t('voiceSetup.buy.title', 'Choisissez votre numéro')}
            description={t('voiceUi.buy.desc', '{{price}} par mois et par numéro, sans engagement. L’agent choisi répond dès le paiement validé.', { price })}
            onClose={onCancel}
            closeLabel={t('voiceUi.close', 'Fermer')}
            footer={(
                <>
                    {!blocked && (
                        <span className="voice-v2__fineprint">
                            <Lock aria-hidden="true" />
                            {t('voiceSetup.buy.stripe', 'Paiement sécurisé par Stripe')}
                        </span>
                    )}
                    <button type="button" className="u-btn u-btn--secondary" onClick={onCancel} disabled={busy}>
                        {blocked ? t('voiceUi.close', 'Fermer') : t('voiceSetup.cancel', 'Annuler')}
                    </button>
                    {!blocked && (
                        <button type="button" className="u-btn u-btn--primary" onClick={pay} disabled={!chosen || busy}>
                            <Lock aria-hidden="true" />
                            {busy ? t('voiceSetup.buy.redirecting', 'Ouverture du paiement…') : t('voiceSetup.buy.pay', 'Payer et activer')}
                        </button>
                    )}
                </>
            )}
        >
            {blocked ? (
                <Banner tone="info">{blocked}</Banner>
            ) : (
                <div className="u-form">
                    <div className="u-field">
                        <span className="u-label">{t('voiceUi.buy.numbersLabel', 'Numéros disponibles')}</span>
                        <ChoiceTiles
                            compact
                            label={t('voiceSetup.buy.title', 'Choisissez votre numéro')}
                            value={chosen}
                            onChange={setChosen}
                            options={numbers.map((n) => ({ value: n.phone_number, title: numberLabel(n) }))}
                        />
                    </div>
                    <div className="voice-v2__buy-grid">
                        <Field
                            label={t('voiceSetup.buy.agent', 'Agent qui répondra')}
                            htmlFor="voice-buy-agent"
                            help={agents.length === 0 ? (
                                <>
                                    {t('voiceSetup.buy.noAgent', 'Aucun agent vocal pour l’instant : le numéro ne répondra qu’une fois relié à un agent.')}{' '}
                                    <Link to="/voice-agents">{t('voiceSetup.createAgentFirst', 'Créez d’abord un agent vocal')}</Link>
                                </>
                            ) : t('voiceUi.buy.agentHelp', 'Vous pourrez le changer à tout moment.')}
                        >
                            <select id="voice-buy-agent" className="u-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                                {agents.map((a) => <option key={a.id} value={a.id}>{a.name || t('voiceSetup.unnamedAgent', 'Agent sans nom')}</option>)}
                                <option value="">{t('voiceSetup.buy.agentLater', 'Choisir plus tard')}</option>
                            </select>
                        </Field>
                        <div className="voice-v2__buy-summary">
                            <span className="u-overline">{t('voiceUi.buy.summary', 'Abonnement')}</span>
                            <strong className="u-num">{t('voiceSetup.buy.price', '{{price}} / mois', { price })}</strong>
                            <ul>
                                <li><Check aria-hidden="true" />{t('voiceSetup.buy.noCommitment', 'Sans engagement, résiliable à tout moment')}</li>
                                <li><Check aria-hidden="true" />{t('voiceSetup.buy.liveOnPayment', 'L’agent répond dès le paiement validé')}</li>
                                <li><Check aria-hidden="true" />{t('voiceSetup.buy.invoice', 'Une facture à chaque échéance')}</li>
                            </ul>
                        </div>
                    </div>
                    <p className="u-help">
                        {t('voiceSetup.buy.keepYourNumber', 'Vous gardez votre numéro actuel ? Demandez à votre opérateur de le renvoyer vers ce numéro : vos clients appellent toujours le même numéro.')}
                    </p>
                    {error && <Banner tone="danger">{error}</Banner>}
                </div>
            )}
        </Modal>
    );
};

export default BuyNumber;
